import { type Accessor, createEffect, createMemo, createSignal, onCleanup } from "solid-js";

const contentOf = (node: HTMLElement): HTMLElement => {
  const content = node.parentElement;
  if (!content) throw new Error("useDock: the player box must be mounted inside the page content");
  return content;
};

// Measured against the page content rather than the box itself, whose rect includes the
// transform of a dock animation still running.
const overSlot = (node: HTMLElement, el: HTMLElement) => {
  const target = el.getBoundingClientRect();
  const content = contentOf(node).getBoundingClientRect();
  node.style.top = `${target.top - content.top}px`;
  node.style.left = `${target.left - content.left}px`;
  node.style.width = `${target.width}px`;
  node.style.height = `${target.height}px`;
};

/**
 * Places the player box over its slot, or docks it in the corner when there is no slot or the
 * slot is under half visible, and animates between the two.
 *
 * Inline, the box is absolutely positioned inside the scrolling page content, so it scrolls with
 * the page natively; a fixed box chasing the slot's rect on scroll lags the compositor by a frame.
 * Docked, the same element switches to fixed. The element is never moved or recreated: either
 * pauses or restarts a playing video in some browsers.
 *
 * The geometry is written to the element by hand rather than through JSX so the box can be
 * measured before and after the switch in one pass.
 */
export const useDock = (box: () => HTMLElement, slot: Accessor<HTMLElement | undefined>) => {
  // Unknown until the first observation, and placed inline meanwhile: a slot that just appeared
  // is on screen (its button was clicked, or a deep link is scrolling to it).
  const [visible, setVisible] = createSignal<boolean>();
  const docked = createMemo(() => !slot() || visible() === false);

  createEffect(() => {
    const el = slot();
    setVisible(undefined);
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.intersectionRatio >= 0.5), {
      threshold: 0.5,
    });
    observer.observe(el);
    onCleanup(() => observer.disconnect());
  });

  let last: { docked: boolean; el: HTMLElement | undefined } | undefined;
  createEffect(() => {
    const node = box();
    const el = slot();
    const isDocked = docked();
    const from = node.getBoundingClientRect();
    for (const animation of node.getAnimations()) animation.cancel();
    node.toggleAttribute("data-docked", isDocked);
    if (isDocked || !el) {
      for (const prop of ["top", "left", "width", "height"]) node.style.removeProperty(prop);
    } else {
      overSlot(node, el);
    }

    const prev = last;
    last = { docked: isDocked, el };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!prev) {
      node.animate(
        [
          { opacity: 0, transform: "scale(0.96)", transformOrigin: "center" },
          { opacity: 1, transform: "none", transformOrigin: "center" },
        ],
        { duration: 240, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }
      );
      return;
    }
    // Only a dock or undock against the same slot flies between the two places. A slot that
    // appears or goes away with a page navigation snaps: its old or new rect is measured before
    // the layout resets the scroll, so a flight would start from the wrong place.
    if (prev.docked === isDocked || prev.el !== el) return;
    const to = node.getBoundingClientRect();
    node.animate(
      [
        {
          transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})`,
        },
        { transform: "none" },
      ],
      { duration: 320, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" }
    );
  });

  // Inline, the box has to follow the slot when the page reflows above it or the window resizes;
  // both resize either the slot or the page content the box is positioned in.
  createEffect(() => {
    const el = slot();
    if (!el || docked()) return;
    const node = box();
    const observer = new ResizeObserver(() => overSlot(node, el));
    observer.observe(el);
    observer.observe(contentOf(node));
    onCleanup(() => observer.disconnect());
  });
};
